import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    LicenseActivationReportDTO,
    useLicenseActivationReportApi,
    useBatchSummaryApi,
} from '../../../api/license-activation-report-api';
import SvgFilterList from '../../../assets/icons/FilterList';
import SvgChevronLeft from '../../../assets/icons/ChevronLeft';
import SvgChevronRight from '../../../assets/icons/ChevronRight';
import SvgFirstPage from '../../../assets/icons/FirstPage';
import SvgLastPage from '../../../assets/icons/LastPage';
import SvgCheck from '../../../assets/icons/Check';
import SvgClose from '../../../assets/icons/Close';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { T, translate } from '../../../components/basic/text';
import { FormikTextField } from '../../../components/formik';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';

export function LicenseActivationReportApp() {
    const reportApi = useLicenseActivationReportApi();
    const batchSummaryApi = useBatchSummaryApi();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    // Filter state
    const [filterProductId, setFilterProductId] = useState<number>(0);
    const [filterBatchNumber, setFilterBatchNumber] = useState<string>('');
    const [filterIsDelivered, setFilterIsDelivered] = useState<boolean | undefined>(undefined);

    const { result, error, busy, initialized, initializing } = reportApi;
    const { result: batchSummaryResult } = batchSummaryApi;

    const productTitle = useCallback((productId: number) => {
        const product = result?.products?.find(p => p.id === productId);
        if (!product) return <span>{productId}</span>;
        return <N>{product.name}</N>;
    }, [result?.products]);

    const columns: IBasicTableColumn<LicenseActivationReportDTO>[] = useMemo(
        () => [
            {
                Header: <T>serial</T>,
                name: 'serial',
                width: 80,
                Cell: ({ row }) => <N>{row.serial}</N>,
            },
            {
                Header: <T>license-code</T>,
                name: 'licenseCode',
                width: 150,
                Cell: ({ row }) => (
                    <span className={row.licenseCode ? '' : 'text-muted'}>
                        {row.licenseCode || '-'}
                    </span>
                ),
            },
            {
                Header: <T>license-key</T>,
                name: 'licenseKey',
                minWidth: 200,
                Cell: ({ row }) => (
                    <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }} title={row.licenseKey}>
                        {row.licenseKey}
                    </span>
                ),
            },
            {
                Header: <T>batch-number</T>,
                name: 'batchNumber',
                width: 180,
                Cell: ({ row }) => (
                    row.batchNumber ? (
                        <span 
                            className="badge bg-info text-dark cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setFilterBatchNumber(row.batchNumber!);
                                setCurrentPage(1);
                            }}
                            title={translate('click-to-filter-by-batch')}
                        >
                            {row.batchNumber}
                        </span>
                    ) : (
                        <span className="badge bg-secondary text-dark">-</span>
                    )
                ),
            },
            {
                Header: <T>product</T>,
                name: 'productId',
                width: 150,
                Cell: ({ row }) => productTitle(row.productId),
            },
            {
                Header: <T>activation-status</T>,
                name: 'isDelivered',
                width: 120,
                Cell: ({ row }) => (
                    <bd.Flex align="center" gap={1}>
                        {row.isDelivered ? (
                            <span className="badge bg-success d-flex align-items-center gap-1">
                                <SvgCheck width={14} height={14} />
                                <T>activated</T>
                            </span>
                        ) : (
                            <span className="badge bg-warning text-dark d-flex align-items-center gap-1">
                                <SvgClose width={14} height={14} />
                                <T>not-activated</T>
                            </span>
                        )}
                    </bd.Flex>
                ),
            },
            {
                Header: <T>activating-user</T>,
                name: 'deliveredToUserName',
                width: 150,
                Cell: ({ row }) => (
                    <span className={row.deliveredToUserName ? '' : 'text-muted'}>
                        {row.deliveredToUserName || '-'}
                    </span>
                ),
            },
            {
                Header: <T>activated-at</T>,
                name: 'deliveredAt',
                width: 150,
                Cell: ({ row }) => (
                    <span className={row.deliveredAt ? '' : 'text-muted'}>
                        {row.deliveredAt ? new Date(row.deliveredAt).toLocaleDateString('fa-IR') : '-'}
                    </span>
                ),
            },
        ],
        [productTitle]
    );

    // Initial load
    useEffect(() => {
        if (initialized || initializing || error) return;
        reportApi.post(null, {
            pageSize: pageSize,
            pageIndex: currentPage,
        });
        batchSummaryApi.post(null, {});
    }, [initialized, initializing, error]);

    // Load filtered data
    useEffect(() => {
        const timer = setTimeout(() => {
            reportApi.post(null, {
                pageSize: pageSize,
                pageIndex: currentPage,
                productId: filterProductId || undefined,
                batchNumber: filterBatchNumber || undefined,
                isDelivered: filterIsDelivered,
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [pageSize, currentPage, filterProductId, filterBatchNumber, filterIsDelivered]);

    // Pagination handlers
    const handlePageChange = useCallback((newPage: number) => {
        setCurrentPage(newPage);
    }, []);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1);
    }, []);

    const totalPages = useMemo(() => Math.ceil((result?.totalCount || 0) / pageSize), [result?.totalCount, pageSize]);

    // Get selected batch summary
    const selectedBatchSummary = useMemo(() => {
        if (!filterBatchNumber || !batchSummaryResult?.batchSummary) return null;
        return batchSummaryResult.batchSummary.find(b => b.batchNumber === filterBatchNumber);
    }, [filterBatchNumber, batchSummaryResult?.batchSummary]);

    if (error) return <InitFailed ex={error} onRetry={() => reportApi.post(null, { pageSize, pageIndex: currentPage })} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="license-activation-report"
                />

                {/* Summary Cards */}
                <bd.Flex gap={3} className="mb-3">
                    <bd.Paper className="p-3 flex-fill">
                        <div className="text-muted small"><T>total-licenses</T></div>
                        <div className="h4 mb-0"><N>{result?.totalCount || 0}</N></div>
                    </bd.Paper>
                    <bd.Paper className="p-3 flex-fill bg-success-subtle">
                        <div className="text-muted small"><T>activated</T></div>
                        <div className="h4 mb-0 text-success"><N>{result?.activatedCount || 0}</N></div>
                    </bd.Paper>
                    <bd.Paper className="p-3 flex-fill bg-warning-subtle">
                        <div className="text-muted small"><T>not-activated</T></div>
                        <div className="h4 mb-0 text-warning"><N>{result?.notActivatedCount || 0}</N></div>
                    </bd.Paper>
                    {selectedBatchSummary && (
                        <bd.Paper className="p-3 flex-fill bg-info-subtle">
                            <div className="text-muted small">
                                <T>batch</T>: {filterBatchNumber}
                            </div>
                            <div className="small">
                                <span className="text-success"><N>{selectedBatchSummary.activatedCount}</N> <T>activated</T></span>
                                {' / '}
                                <span className="text-warning"><N>{selectedBatchSummary.notActivatedCount}</N> <T>not-activated</T></span>
                            </div>
                        </bd.Paper>
                    )}
                </bd.Flex>

                <bd.Paper className="h-100 p-3">
                    <Formik
                        initialValues={{ 
                            productId: filterProductId, 
                            batchNumber: filterBatchNumber,
                            isDelivered: filterIsDelivered === undefined ? '' : filterIsDelivered ? 'true' : 'false'
                        }}
                        enableReinitialize
                        onSubmit={values => {
                            setFilterProductId(values.productId);
                            setFilterBatchNumber(values.batchNumber);
                            setFilterIsDelivered(
                                values.isDelivered === '' ? undefined : values.isDelivered === 'true'
                            );
                            setCurrentPage(1);
                        }}
                    >
                        <Form>
                            <bd.Flex align="center" gap={2} wrap>
                                <FormikTextField
                                    name="productId"
                                    label={<T>product</T>}
                                    items={[
                                        { id: 0, title: translate('all') },
                                        ...(result?.products?.map(p => ({ id: p.id, title: p.name })) || []),
                                    ]}
                                    width="11rem"
                                />
                                <FormikTextField
                                    name="batchNumber"
                                    label={<T>batch-number</T>}
                                    items={[
                                        { id: '', title: translate('all') },
                                        ...(result?.batchNumbers?.map(b => ({ id: b, title: b })) || []),
                                    ]}
                                    width="15rem"
                                />
                                <FormikTextField
                                    name="isDelivered"
                                    label={<T>activation-status</T>}
                                    items={[
                                        { id: '', title: translate('all') },
                                        { id: 'true', title: translate('activated') },
                                        { id: 'false', title: translate('not-activated') },
                                    ]}
                                    width="10rem"
                                />
                                <LoadingButton
                                    variant="contained"
                                    type="submit"
                                    className="mt-2 min-w-80"
                                    icon={<SvgFilterList />}
                                    label={<T>filter</T>}
                                    loading={busy}
                                />
                                {(filterProductId > 0 || filterBatchNumber || filterIsDelivered !== undefined) && (
                                    <bd.Button
                                        variant="outline"
                                        color="secondary"
                                        size="sm"
                                        className="mt-2"
                                        onClick={() => {
                                            setFilterProductId(0);
                                            setFilterBatchNumber('');
                                            setFilterIsDelivered(undefined);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <T>clear-filters</T>
                                    </bd.Button>
                                )}
                            </bd.Flex>
                        </Form>
                    </Formik>

                    <bd.BasicTable
                        columns={columns}
                        data={result?.licenses || []}
                        className="mx-n3 mt-3"
                        cellCSS={({ row }) => {
                            return row.isDelivered ? 'bg-success-subtle bg-opacity-25' : '';
                        }}
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    {/* Pagination Controls */}
                    {(result?.totalCount || 0) > 0 && (
                        <bd.Flex justify="between" align="center" className="p-3 border-top">
                            <bd.Flex align="center" gap={2}>
                                <span>
                                    <T>showing</T> {((currentPage - 1) * pageSize) + 1} <T>to</T> {Math.min(currentPage * pageSize, result?.totalCount || 0)} <T>of</T> <N>{result?.totalCount || 0}</N>
                                </span>
                                <select
                                    className="form-select"
                                    value={pageSize}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handlePageSizeChange(Number(e.target.value))}
                                    style={{ width: '80px' }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <T>per-page</T>
                            </bd.Flex>
                            
                            <bd.Flex align="center" gap={1}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                    title={translate('first-page')}
                                >
                                    <SvgFirstPage className="rtl-rotate-180" />
                                </bd.Button>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    title={translate('previous-page')}
                                >
                                    <SvgChevronLeft className="rtl-rotate-180" />
                                </bd.Button>
                                <span className="px-2">
                                    <T>page</T> <N>{currentPage}</N> <T>of</T> <N>{totalPages}</N>
                                </span>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    title={translate('next-page')}
                                >
                                    <SvgChevronRight className="rtl-rotate-180" />
                                </bd.Button>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                    title={translate('last-page')}
                                >
                                    <SvgLastPage className="rtl-rotate-180" />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    )}
                </bd.Paper>
            </div>
        </TProvider>
    );
}

export default LicenseActivationReportApp;

